import styled, { css, keyframes } from 'styled-components';

const STROKE_COLOR = '#c9443a';
const FILL_COLOR = '#E25241';
const SIZE = 100;

const PIN_TIP_X = 40;
const PIN_TIP_Y = 40;
const PIN_CIRCLE_CENTER_X = PIN_TIP_X;
const PIN_CIRCLE_CENTER_Y = 23;

type Props = {
  movable?: boolean;
  withPin?: boolean;
};

export const LocationMarker = ({ movable = false, withPin = true }: Props) => (
  <Svg width={SIZE} height={SIZE} viewBox="0 0 80 80">
    {movable && (
      <>
        <RippleCircle $delay={0} />
        <RippleCircle $delay={1100} />
        <RippleCircle $delay={2200} />
        <CrosshairWithArrows />
      </>
    )}
    {withPin && <MainPin $animated={movable} />}
  </Svg>
);

const MainPin = ({ $animated }: { $animated: boolean }) => (
  <PinGroup $animated={$animated}>
    <path
      d="M40 40 C35 37 28 31 28 23 C28 16.373 33.373 11 40 11 C46.627 11 52 16.373 52 23 C52 31 45 37 40 40 Z"
      fill={FILL_COLOR}
      stroke={STROKE_COLOR}
      strokeWidth="1.5"
    />
    <circle cx={PIN_CIRCLE_CENTER_X} cy={PIN_CIRCLE_CENTER_Y} r="6" fill="white" />
  </PinGroup>
);

const CrosshairWithArrows = () => (
  <CrosshairGroup>
    {/* Horizontal lines */}
    <line
      x1="25"
      y1={PIN_TIP_Y}
      x2="35"
      y2={PIN_TIP_Y}
      stroke={FILL_COLOR}
      strokeWidth="2.5"
      strokeLinecap="round"
    />
    <line
      x1="45"
      y1={PIN_TIP_Y}
      x2="55"
      y2={PIN_TIP_Y}
      stroke={FILL_COLOR}
      strokeWidth="2.5"
      strokeLinecap="round"
    />
    {/* Vertical lines */}
    <line
      x1={PIN_TIP_X}
      y1={PIN_TIP_Y - 5}
      x2={PIN_TIP_X}
      y2={PIN_TIP_Y - 15}
      stroke={FILL_COLOR}
      strokeWidth="2.5"
      strokeLinecap="round"
    />
    <line
      x1={PIN_TIP_X}
      y1={PIN_TIP_Y + 5}
      x2={PIN_TIP_X}
      y2={PIN_TIP_Y + 15}
      stroke={FILL_COLOR}
      strokeWidth="2.5"
      strokeLinecap="round"
    />
    {/* Arrow heads */}
    <path d={`M21 ${PIN_TIP_Y} L26 ${PIN_TIP_Y - 4} L26 ${PIN_TIP_Y + 4} Z`} fill={FILL_COLOR} />
    <path d={`M59 ${PIN_TIP_Y} L54 ${PIN_TIP_Y - 4} L54 ${PIN_TIP_Y + 4} Z`} fill={FILL_COLOR} />
    <path
      d={`M${PIN_TIP_X} ${PIN_TIP_Y - 19} L${PIN_TIP_X - 4} ${PIN_TIP_Y - 14} L${PIN_TIP_X + 4} ${PIN_TIP_Y - 14} Z`}
      fill={FILL_COLOR}
    />
    <path
      d={`M${PIN_TIP_X} ${PIN_TIP_Y + 19} L${PIN_TIP_X - 4} ${PIN_TIP_Y + 14} L${PIN_TIP_X + 4} ${PIN_TIP_Y + 14} Z`}
      fill={FILL_COLOR}
    />
  </CrosshairGroup>
);

const Svg = styled.svg`
  overflow: visible;
`;

const rippleAnimation = keyframes`
  0% {
    transform: translate(${PIN_TIP_X}px, ${PIN_TIP_Y}px) scale(1) translate(-${PIN_TIP_X}px, -${PIN_TIP_Y}px);
    opacity: 0.25;
  }
  100% {
    transform: translate(${PIN_TIP_X}px, ${PIN_TIP_Y}px) scale(7.33) translate(-${PIN_TIP_X}px, -${PIN_TIP_Y}px);
    opacity: 0;
  }
`;

const RippleCircle = styled.circle.attrs({
  cx: PIN_TIP_X,
  cy: PIN_TIP_Y,
  r: 3,
  fill: 'none',
  stroke: FILL_COLOR,
  strokeWidth: 1.5,
})<{ $delay: number }>`
  animation: ${rippleAnimation} 3300ms cubic-bezier(0, 0, 0.2, 1) infinite;
  animation-delay: ${({ $delay }) => $delay}ms;
`;

const crosshairPulse = keyframes`
  0%, 100% {
    opacity: 0.5;
  }
  50% {
    opacity: 0.2;
  }
`;

const CrosshairGroup = styled.g`
  animation: ${crosshairPulse} 2700ms ease-in-out infinite;
`;

// Spring-like bounce animation
const pinEnter = keyframes`
  0% {
    transform: translateY(-8px);
    opacity: 0;
  }
  20% {
    opacity: 1;
  }
  25% {
    transform: translateY(3px);
  }
  50% {
    transform: translateY(-2px);
  }
  75% {
    transform: translateY(0.5px);
  }
  100% {
    transform: translateY(0);
  }
`;

const PinGroup = styled.g<{ $animated: boolean }>`
  ${({ $animated }) =>
    $animated &&
    css`
      animation: ${pinEnter} 600ms forwards cubic-bezier(0.34, -0.37, 0.61, 1.46);
    `}
`;
